import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { getRunningWebBuildId } from '../versioning/webBuildVersion';
import { getClientDeviceId } from './clientDeviceId';

export type ClientPresencePlatform = 'web' | 'ios' | 'android';

export type ClientAuditMeta = {
  platform: ClientPresencePlatform;
  app_version: string;
  build?: string;
  web_build_id?: string;
  device_label?: string;
};

export type ClientPresenceRpcPayload = {
  organizationId: string;
  deviceId: string;
  userId: string;
  branchId?: string | null;
  platform: ClientPresencePlatform;
  appVersion: string;
  nativeBuild?: string;
  webBuildId?: string;
  deviceLabel?: string;
  userAgent?: string;
};

function resolvePlatform(): ClientPresencePlatform {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
}

function resolveNativeBuild(): string | undefined {
  if (Platform.OS === 'ios') {
    const bn = Constants.expoConfig?.ios?.buildNumber;
    return bn != null && String(bn).trim() !== '' ? String(bn) : undefined;
  }
  if (Platform.OS === 'android') {
    const vc = Constants.expoConfig?.android?.versionCode;
    return vc != null ? String(vc) : undefined;
  }
  return undefined;
}

function resolveDeviceLabel(): string | undefined {
  if (Platform.OS === 'web') return undefined;
  const parts = [Device.manufacturer, Device.modelName].filter(Boolean);
  if (parts.length === 0) return undefined;
  return parts.join(' ').slice(0, 128);
}

function resolveUserAgent(): string | undefined {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return undefined;
  const ua = navigator.userAgent;
  if (!ua?.trim()) return undefined;
  return ua.trim().slice(0, 200);
}

/** Metadatos de cliente para audit login (sin device id). */
export async function buildClientAuditMeta(): Promise<ClientAuditMeta> {
  const platform = resolvePlatform();
  const app_version = Constants.expoConfig?.version ?? '?';
  const build = resolveNativeBuild();
  const web_build_id = platform === 'web' ? getRunningWebBuildId() ?? undefined : undefined;
  const device_label = resolveDeviceLabel();
  return {
    platform,
    app_version,
    ...(build ? { build } : {}),
    ...(web_build_id ? { web_build_id } : {}),
    ...(device_label ? { device_label } : {}),
  };
}

export async function buildClientPresencePayload(params: {
  organizationId: string;
  userId: string;
  branchId?: string | null;
}): Promise<ClientPresenceRpcPayload> {
  const platform = resolvePlatform();
  const deviceId = await getClientDeviceId();
  const appVersion = Constants.expoConfig?.version ?? '?';
  const nativeBuild = resolveNativeBuild();
  const webBuildId = platform === 'web' ? getRunningWebBuildId() ?? undefined : undefined;
  const deviceLabel = resolveDeviceLabel();
  const userAgent = resolveUserAgent();

  return {
    organizationId: params.organizationId,
    deviceId,
    userId: params.userId,
    branchId: params.branchId ?? null,
    platform,
    appVersion,
    ...(nativeBuild ? { nativeBuild } : {}),
    ...(webBuildId ? { webBuildId } : {}),
    ...(deviceLabel ? { deviceLabel } : {}),
    ...(userAgent ? { userAgent } : {}),
  };
}
